import { useScopedRestaurant } from '../../../../../hooks/restaurantViewScope';
import { recipeWriteVersion, StaleRecipeWriteError } from '../../../../../services/recipeWriteConflict';
import { usePracticeDataset } from '../../../../onboarding/practice/practiceDataset';
import { isPracticeId } from '../../../../onboarding/practice/practiceGuards';
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLoadCostPrice, useLoadOrderHistory, useLoadRevenueHistory, useLoadSubrecipes } from "../../../../../utils/dataLoadingUtils";
import { useAppUserDefaultStore } from "../../../../../stores/appUserDefaultStore";
import { useCostPriceStore } from "../../../../../stores/appCostPriceStore";
import { copyCostPriceIngredients, mergeCostPriceClipboard, CostPriceCopySelection } from '../../../../../utils/costPriceClipboard';
import { useCostPriceCategoriesStore } from "../../../../../stores/appCostPriceCategoriesStore";
import { useOrderHistory } from "../../../../../stores/appOrderHistoryStore";
import { useRevenueHistory } from "../../../../../stores/appRevenueHistoryStore";
import { useSubrecipesStore } from "../../../../../stores/appSubrecipesStore";
import { enrichCostPriceWithCurrentPrices, getEffectiveMenuPrice, calculateCostPercentage, getQuantitySoldLast14Days, findLatestMenuPrice, getChoiceAvgUnitPrice, getByoChoiceAggregates, findOrCreateChoiceCostPrice, COST_PRICE_TYPE_CHOICE, choiceIngredientKey, applyByoQuantityOverrides } from "../../../../../utils/costPriceUtils";
import { useByRestaurantCostData } from "../../../../../hooks/useByRestaurantCostData";
import { useSourceRestaurantDataStore } from "../../../../../stores/appSourceRestaurantDataStore";
import { groupIngredientsBySubrecipe, getNestedCostPriceInfo } from "../../../../../utils/ingredientGrouping";
import { updateCostPriceAnalysis, fetchRevenueItemNames, fetchCostPriceAnalysisById, deleteCostPriceAnalysis, toIngredientUpdatePayload, CostPriceAnalysis, CostPriceIngredient, IngredientAlternative, ByoCostBreakdown } from "../../../../../services/costPrice/costPriceService";
import type { SubrecipeIngredient } from "../../../../../services/subrecipes/subrecipesService";
import { linkedCostPriceRow } from "../../review/customLinkAdapter";
import type { SwapCandidate } from "../../review/reviewAdapter";
import { createCostPriceCategory, updateCategoryPositions, CostPriceCategory } from "../../../../../services/costPrice/costPriceCategoriesService";
import { calculateMenuEngineeringMatrix, calculateAverageUnitsSold } from "../../../../../services/costPrice/menuEngineeringService";
import { CostTargets, DEFAULT_COST_TARGETS, getCostTargetForDishType, isCostBad as isCostBadFor } from "../../../../../utils/costTargets";
import { transformCostPriceListToDishData } from "../../new/dataAdapter";
import { Platform } from "../../../../../types/Platforms";
import { productKey } from "../../../../../utils/reports/shared";
import { useAuth } from "../../../../../services/authentication/authContext";
import { canAccessCostPriceAutomation } from "../../../../../services/accessManagement/accessManagement";

// BYO: group key for the "Unassigned" choices bucket (choices with no add-on
// category). Real categories key on their `id`.
export const BYO_UNASSIGNED_KEY = '__unassigned__';

// Helper: unit price for an ingredient (converted: /kg, /L, /pc)
export const getIngredientUnitPrice = (ingredient: any): { value: number; unit: string } | null => {
    if (!ingredient.currentPrice) return null;
    const stdUnit = ingredient.quantityInfo?.stdUnit || '';
    if (stdUnit === 'g') return { value: ingredient.currentPrice * 1000, unit: 'kg' };
    if (stdUnit === 'ml') return { value: ingredient.currentPrice * 1000, unit: 'L' };
    if (stdUnit === 'pc' || stdUnit === 'piece' || stdUnit === 'pcs') return { value: ingredient.currentPrice, unit: 'pc' };
    return { value: ingredient.currentPrice, unit: stdUnit };
};

// Helper: check if platform is a valid known platform
const isKnownPlatform = (platformName: string): boolean => {
    const validPlatforms = Object.values(Platform).map(p => p.toString().toLowerCase());
    return validPlatforms.includes(platformName?.toLowerCase() || '');
};

// Helper: platform display info for products
export const getProductPlatformDisplay = (platform: string, customPlatformName?: string): { showLogo: boolean; displayName: string; platformForLogo?: Platform } => {
    if (customPlatformName && isKnownPlatform(customPlatformName)) {
        return { showLogo: true, displayName: customPlatformName, platformForLogo: customPlatformName.toLowerCase() as Platform };
    }
    if (platform && isKnownPlatform(platform)) {
        return { showLogo: true, displayName: platform, platformForLogo: platform.toLowerCase() as Platform };
    }
    const displayName = customPlatformName || platform || '';
    return { showLogo: false, displayName };
};

// Build the ingredient update payload (shared across all save/delete handlers).
// Delegates to the shared `toIngredientUpdatePayload` serializer so any new
// ingredient field (e.g. sourceRestaurantId) is preserved across edits.
const mapIngredientForUpdate = (ing: CostPriceIngredient) => toIngredientUpdatePayload(ing);

// `byoParentId`: set when this modal shows a CHOICE opened from a BYO dish.
// In that dish context, quantity edits become per-dish overrides stored on the
// parent dish (`byoChoiceOverrides`) instead of edits to the shared CHOICE;
// adds/removes/waste edits stay shared. Editing a CHOICE standalone (Add-ons
// tab) always edits the shared values.
export function useCostPriceDetail(costPriceId: string, onClose: () => void, costTargets: CostTargets = DEFAULT_COST_TARGETS, defaultWastePercentage: number = 0, byoParentId?: string) {
    const { t } = useTranslation();
    // --- UI state ---
    const [isPanelVisible, setIsPanelVisible] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);
    const [showAddIngredients, setShowAddIngredients] = useState(false);
    const [selectedSubRecipeName, setSelectedSubRecipeName] = useState<string | null>(null);
    // Snapshot of the ingredient whose Alternatives editor is open (view-swap).
    // A snapshot (not an id) because every save regenerates ingredient row ids —
    // an id would go stale after the first edit and collapse the open view. The
    // snapshot's `alternatives` is the working copy, updated optimistically.
    const alternativesSnapshot = useRef<CostPriceAnalysis | null>(null);
    const [alternativesIngredient, setAlternativesIngredient] = useState<CostPriceIngredient | null>(null);
    // Snapshot of the CUSTOM ingredient row whose link picker is open. Snapshot for
    // the same reason as above — the row id it carries is only used to find the row
    // in a freshly fetched analysis inside the single write it triggers.
    const [customLinkIngredient, setCustomLinkIngredient] = useState<CostPriceIngredient | null>(null);
    const [editingItemCategory, setEditingItemCategory] = useState<{ itemName: string; currentCategoryId: string } | null>(null);
    // BYO add-on category assignment: the choice (by name) whose add-on category
    // the user is editing from the choices list. Operates on that choice's CHOICE
    // analysis (lazily created), not the current BYO analysis.
    const [editingChoiceCategory, setEditingChoiceCategory] = useState<{ choiceName: string; currentCategoryId: string } | null>(null);
    const [showEditRevenueLinkModal, setShowEditRevenueLinkModal] = useState(false);
    const [revenueLinkRefreshFailed, setRevenueLinkRefreshFailed] = useState(false);
    const [revenueItems, setRevenueItems] = useState<string[]>([]);
    const [inlineIngredientQuantities, setInlineIngredientQuantities] = useState<Record<string, string>>({});
    const [inlineIngredientWaste, setInlineIngredientWaste] = useState<Record<string, string>>({});
    const [clipboardFeedback, setClipboardFeedback] = useState('');
    const [isPasting, setIsPasting] = useState(false);
    const pasteInFlight = useRef(false);
    useEffect(() => {
        if (!clipboardFeedback) return;
        const timer = setTimeout(() => setClipboardFeedback(''), 5000);
        return () => clearTimeout(timer);
    }, [clipboardFeedback]);
    const [inlineNestedMenuItemQuantities, setInlineNestedMenuItemQuantities] = useState<Record<string, string>>({});
    const [inlineSubrecipeQuantities, setInlineSubrecipeQuantities] = useState<Record<string, string>>({});
    const [confirmingDeleteIngredientId, setConfirmingDeleteIngredientId] = useState<string | null>(null);
    const [confirmingDeleteNestedMenuItemId, setConfirmingDeleteNestedMenuItemId] = useState<string | null>(null);
    const [confirmingDeleteSubrecipeName, setConfirmingDeleteSubrecipeName] = useState<string | null>(null);
    const [showEditMenuPriceModal, setShowEditMenuPriceModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showTypeSelector, setShowTypeSelector] = useState(false);
    const [clearSectionTarget, setClearSectionTarget] = useState<'ingredients' | 'nested' | 'subrecipes' | null>(null);
    // BYO = build-your-own. Weighting is now PER add-on category. Every category
    // starts weighted (its weighted-average contribution is folded into the
    // headline). When the user picks a choice in a category, only THAT category
    // drops out of weighted mode and contributes the picked choice(s) instead —
    // the other categories stay weighted, so the composed number stays realistic.
    // `unweightedCategoryKeys` holds the group keys (category.id, or
    // '__unassigned__' for the unassigned group) currently NOT weighted.
    // An unweighted category with no pick contributes €0 ("customer skipped it").
    // Nothing here is persisted.
    const [unweightedCategoryKeys, setUnweightedCategoryKeys] = useState<Set<string>>(new Set());
    const [composerSelection, setComposerSelection] = useState<Set<string>>(new Set());
    // Derived global flags (kept for the modal / section API surface):
    //   weightedMode = every category weighted (headline equals the full weighted
    //                  average — identical to the old default view).
    //   composerMode = at least one category unweighted, so SummaryCards reflect
    //                  the live composed selection.
    const weightedMode = unweightedCategoryKeys.size === 0;
    const composerMode = !weightedMode;

    // --- Data loading ---
    useLoadCostPrice();
    useLoadOrderHistory();
    useLoadRevenueHistory();
    useLoadSubrecipes();

    // --- Store connections ---
    const selectedRestaurant = useScopedRestaurant();
    const restaurantId = selectedRestaurant?.id;
    const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);
    const practiceData = usePracticeDataset(isPracticeId(costPriceId));
    const liveCostPriceStore = useCostPriceStore();
    const costPriceStore = useMemo(
        () => practiceData ? { ...liveCostPriceStore, ...practiceData } : liveCostPriceStore,
        [practiceData, liveCostPriceStore],
    );
    const liveOrderHistoryStore = useOrderHistory();
    const orderHistoryStore = practiceData ? { ...liveOrderHistoryStore, ...practiceData } : liveOrderHistoryStore;
    const liveRevenueHistoryStore = useRevenueHistory();
    const revenueHistoryStore = practiceData ? { ...liveRevenueHistoryStore, ...practiceData } : liveRevenueHistoryStore;
    const liveCategoriesStore = useCostPriceCategoriesStore();
    const categoriesStore = useMemo(
        () => practiceData ? { ...liveCategoriesStore, ...practiceData } : liveCategoriesStore,
        [practiceData, liveCategoriesStore],
    );
    const liveSubrecipesById = useSubrecipesStore(s => s.subrecipesById);
    const subrecipesById = practiceData?.subrecipesById ?? liveSubrecipesById;
    // Cross-restaurant sourcing: sibling data sets (used to resolve sibling
    // subrecipe display info; loading is driven by useByRestaurantCostData below).
    const sourceDataByRestaurant = useSourceRestaurantDataStore(s => s.dataByRestaurant);

    if (practiceData) defaultWastePercentage = 0;

    // --- Permissions ---
    const { backendUser, accountSettings } = useAuth();
    const canAccessAutomation = canAccessCostPriceAutomation(backendUser, accountSettings);

    // --- Load revenue items ---
    useEffect(() => {
        if (isPracticeId(costPriceId)) { setRevenueItems(practiceData?.revenueItems ?? []); return; }
        if (!restaurantId) return;
        fetchRevenueItemNames(restaurantId)
            .then(setRevenueItems)
            .catch(err => console.error('Failed to load revenue items:', err));
    }, [restaurantId, costPriceId, practiceData]);

    // --- Slide-in animation & reset ---
    useEffect(() => {
        document.body.style.overflow = "hidden";
        setIsPanelVisible(true);
        setEditingItemCategory(null);
        setShowEditRevenueLinkModal(false);
        setRevenueLinkRefreshFailed(false);
        setShowAddIngredients(false);
        setAlternativesIngredient(null);
        setInlineIngredientQuantities({});
        setInlineIngredientWaste({});
        setInlineNestedMenuItemQuantities({});
        setInlineSubrecipeQuantities({});
        setConfirmingDeleteIngredientId(null);
        setConfirmingDeleteNestedMenuItemId(null);
        setConfirmingDeleteSubrecipeName(null);
        return () => { document.body.style.overflow = ""; };
    }, []);

    // --- Auto-revert delete confirmations ---
    useEffect(() => {
        if (!confirmingDeleteIngredientId) return;
        const t = setTimeout(() => setConfirmingDeleteIngredientId(null), 2000);
        return () => clearTimeout(t);
    }, [confirmingDeleteIngredientId]);

    useEffect(() => {
        if (!confirmingDeleteNestedMenuItemId) return;
        const t = setTimeout(() => setConfirmingDeleteNestedMenuItemId(null), 2000);
        return () => clearTimeout(t);
    }, [confirmingDeleteNestedMenuItemId]);

    useEffect(() => {
        if (!confirmingDeleteSubrecipeName) return;
        const t = setTimeout(() => setConfirmingDeleteSubrecipeName(null), 2000);
        return () => clearTimeout(t);
    }, [confirmingDeleteSubrecipeName]);

    // --- BYO dish context (CHOICE opened from a BYO dish) ---
    // The store holds the SHARED choice values; the parent dish may carry
    // per-dish quantity overrides for this choice. Display (and cost) the
    // override-applied copy so the editor shows this dish's portions.
    // Store methods close over the live Zustand state, even when its data fields
    // are replaced by a practice dataset. Resolve from the editor's scoped list.
    const sharedAnalysis = costPriceStore.costPriceList.find(analysis => analysis.id === costPriceId);
    const parentAnalysis = byoParentId ? costPriceStore.costPriceList.find(analysis => analysis.id === byoParentId) : undefined;
    const isChoiceWithDishContext = !!(sharedAnalysis?.costPriceType === COST_PRICE_TYPE_CHOICE && parentAnalysis);
    const choiceOverrides = isChoiceWithDishContext
        ? parentAnalysis?.byoChoiceOverrides?.[sharedAnalysis!.costPriceName]
        : undefined;

    // --- Enriched analysis ---
    // Pass revenueHistory so BYO dishes get their weighted breakdown + weighted
    // menu price attached. Non-BYO analyses are unaffected.
    const rawAnalysis = useMemo(() => {
        if (!sharedAnalysis || !choiceOverrides) return sharedAnalysis;
        return { ...sharedAnalysis, ingredients: applyByoQuantityOverrides(sharedAnalysis.ingredients, choiceOverrides) };
    }, [sharedAnalysis, choiceOverrides]);

    // Section row keys (ingredient.id / subrecipeId / nestedCostPriceId) whose
    // quantity is overridden for this dish — drives the "custom for this dish"
    // badge + reset in the section components.
    const overriddenSectionKeys = useMemo(() => {
        const keys = new Set<string>();
        if (!choiceOverrides || !sharedAnalysis) return keys;
        for (const ing of sharedAnalysis.ingredients) {
            if (choiceOverrides[choiceIngredientKey(ing)] === undefined) continue;
            if (ing.subrecipeId && !ing.articleId) keys.add(ing.subrecipeId);
            else if (ing.nestedCostPriceId) keys.add(ing.nestedCostPriceId);
            else keys.add(ing.id);
        }
        return keys;
    }, [choiceOverrides, sharedAnalysis]);

    // Cross-restaurant: load only the sibling restaurants this analysis borrows
    // from, then build the keyed maps so each borrowed ingredient prices live
    // from its source restaurant's data.
    const analysesForSiblings = useMemo(() => (rawAnalysis ? [rawAnalysis] : []), [rawAnalysis]);
    const byRestaurant = useByRestaurantCostData(analysesForSiblings, restaurantId);

    const enrichedAnalysis = rawAnalysis
        ? enrichCostPriceWithCurrentPrices(rawAnalysis, orderHistoryStore.orderHistoryList, {
            customIngredients: costPriceStore.customIngredients,
            showExVat: showExVatOrders,
            allCostPriceAnalyses: costPriceStore.costPriceList,
            allSubrecipes: subrecipesById,
            revenueHistory: revenueHistoryStore.revenueHistoryList,
            byRestaurant,
            currentRestaurantId: restaurantId,
            defaultWastePercentage,
          })
        : null;

    // When a CHOICE is opened from a BYO dish (dish context), its price/units
    // should reflect what that choice sold for WITHIN this dish, not the global
    // cross-dish average that getEffectiveMenuPrice/getChoiceAvgUnitPrice return.
    // This mirrors the per-pick price the parent dish's composer already uses
    // (byo.choices[].unitPrice), keeping the choice detail in sync with the dish.
    const dishScopedChoiceAgg = useMemo(() => {
        if (!isChoiceWithDishContext || !parentAnalysis?.menuItemName || !sharedAnalysis) return null;
        const aggs = getByoChoiceAggregates(parentAnalysis.menuItemName, revenueHistoryStore.revenueHistoryList);
        return aggs?.choices.get(sharedAnalysis.costPriceName) ?? null;
    }, [isChoiceWithDishContext, parentAnalysis, sharedAnalysis, revenueHistoryStore.revenueHistoryList]);

    // Dish-scoped effective menu price for the choice. A custom price on the
    // CHOICE still wins (return null → getEffectiveMenuPrice priority 2 handles it).
    const dishScopedChoicePrice = useMemo(() => {
        if (!dishScopedChoiceAgg || dishScopedChoiceAgg.qtySold <= 0 || !sharedAnalysis) return null;
        if (sharedAnalysis.customMenuPrice) return null;
        return {
            menuPrice: dishScopedChoiceAgg.revenue / dishScopedChoiceAgg.qtySold,
            menuExVatPrice: dishScopedChoiceAgg.revenueExVat / dishScopedChoiceAgg.qtySold,
            date: new Date(sharedAnalysis.updatedAt),
            source: 'Choice avg (this dish)',
            isCustom: false,
            isCustomItem: false,
        };
    }, [dishScopedChoiceAgg, sharedAnalysis]);

    const effectiveMenuPrice = dishScopedChoicePrice ?? (enrichedAnalysis
        ? getEffectiveMenuPrice(enrichedAnalysis, revenueHistoryStore.revenueHistoryList)
        : null);

    const menuPriceForCalculation = effectiveMenuPrice
        ? (showExVatOrders ? effectiveMenuPrice.menuExVatPrice : effectiveMenuPrice.menuPrice)
        : 0;

    const costPercentage = enrichedAnalysis && menuPriceForCalculation > 0
        ? calculateCostPercentage(enrichedAnalysis.totalCost, menuPriceForCalculation)
        : null;

    const currentRevenuePrice = enrichedAnalysis?.menuItemName
        ? findLatestMenuPrice(enrichedAnalysis.menuItemName, revenueHistoryStore.revenueHistoryList)
        : null;

    // --- Matrix ---
    const matrix = useMemo(() => {
        if (!enrichedAnalysis || !effectiveMenuPrice || !restaurantId) return null;
        const popularity = enrichedAnalysis.menuItemName
            ? getQuantitySoldLast14Days(enrichedAnalysis.menuItemName, revenueHistoryStore.revenueHistoryList)
            : 0;
        const margin = effectiveMenuPrice.menuPrice - enrichedAnalysis.totalCost;
        const marginPercentage = effectiveMenuPrice.menuPrice > 0 ? (margin / effectiveMenuPrice.menuPrice) * 100 : 0;

        const tempDishData = {
            id: enrichedAnalysis.id,
            dishName: enrichedAnalysis.costPriceName,
            // Matrix maths only — this row is never rendered, so the raw name is the display name.
            displayName: enrichedAnalysis.costPriceName,
            category: "", categoryColor: "", isDefaultCategory: false,
            matrix: null as any, margin, marginPercentage,
            costPercentage: costPercentage || 0, popularity,
            isPopularityHigh: false,
            price: effectiveMenuPrice.menuPrice,
            priceExVat: effectiveMenuPrice.menuExVatPrice,
            totalCost: enrichedAnalysis.totalCost,
            isPlaceholder: false, isUncategorizedPlaceholder: false,
            revenueSourceType: undefined,
            costPriceName: enrichedAnalysis.costPriceName,
            menuItemName: enrichedAnalysis.menuItemName,
            categoryId: enrichedAnalysis.categoryId,
            ingredients: enrichedAnalysis.ingredients,
            isCustomItem: enrichedAnalysis.isCustomItem,
            customMenuPrice: enrichedAnalysis.customMenuPrice,
            customMenuPriceExVat: enrichedAnalysis.customMenuPriceExVat,
            customItemPrice: enrichedAnalysis.customItemPrice,
            customItemPriceExVat: enrichedAnalysis.customItemPriceExVat,
            restaurantId: enrichedAnalysis.restaurantId,
            createdAt: enrichedAnalysis.createdAt,
            updatedAt: enrichedAnalysis.updatedAt,
        };

        const allDishData = transformCostPriceListToDishData(
            costPriceStore.costPriceList, revenueHistoryStore.revenueHistoryList,
            revenueItems, categoriesStore.categories, showExVatOrders, restaurantId
        );
        const averageUnitsSold = calculateAverageUnitsSold(allDishData);
        return calculateMenuEngineeringMatrix(tempDishData, averageUnitsSold, costTargets);
    }, [enrichedAnalysis, effectiveMenuPrice, costPercentage, revenueHistoryStore.revenueHistoryList, costPriceStore.costPriceList, revenueItems, categoriesStore.categories, showExVatOrders, restaurantId, costTargets]);

    // --- Category ---
    const category = useMemo(() => {
        if (!enrichedAnalysis?.categoryId) return null;
        return categoriesStore.byId(enrichedAnalysis.categoryId);
    }, [enrichedAnalysis?.categoryId, categoriesStore]);

    // --- BYO add-on categories (scope='addon'), used to group the choices list ---
    const addonCategories = useMemo(
        () => categoriesStore.categories
            .filter(c => (c.scope ?? 'revenue') === 'addon')
            .slice()
            .sort((a, b) => a.position - b.position),
        [categoriesStore.categories]
    );

    // choiceName -> add-on categoryId, resolved from each choice's CHOICE analysis.
    const choiceCategoryIdByName = useMemo(() => {
        const m = new Map<string, string | undefined>();
        for (const a of costPriceStore.costPriceList) {
            if (a.costPriceType === COST_PRICE_TYPE_CHOICE && a.restaurantId === restaurantId) {
                m.set(a.costPriceName, a.categoryId);
            }
        }
        return m;
    }, [costPriceStore.costPriceList, restaurantId]);

    // Choices grouped by add-on category: assigned categories in position order
    // first, an "Unassigned" group (category === null) last. Empty when not BYO.
    const byoChoiceGroups = useMemo(() => {
        type Choices = ByoCostBreakdown['choices'];
        const groups: { category: CostPriceCategory | null; choices: Choices }[] = [];
        const byo = enrichedAnalysis?.byo;
        if (!byo) return groups;
        const catById = new Map(addonCategories.map(c => [c.id, c]));
        const grouped = new Map<string, Choices>();
        const unassigned: Choices = [];
        for (const choice of byo.choices) {
            const catId = choiceCategoryIdByName.get(choice.name);
            const cat = catId ? catById.get(catId) : undefined;
            if (cat) {
                if (!grouped.has(cat.id)) grouped.set(cat.id, []);
                grouped.get(cat.id)!.push(choice);
            } else {
                unassigned.push(choice);
            }
        }
        // Within each group, most-picked (last 2 months) first.
        const byPicksDesc = (a: Choices[number], b: Choices[number]) => b.qtySold - a.qtySold;
        for (const cat of addonCategories) {
            const cs = grouped.get(cat.id);
            if (cs && cs.length) groups.push({ category: cat, choices: cs.slice().sort(byPicksDesc) });
        }
        if (unassigned.length) groups.push({ category: null, choices: unassigned.slice().sort(byPicksDesc) });
        return groups;
    }, [enrichedAnalysis, addonCategories, choiceCategoryIdByName]);

    // Group key for a choice (category.id, or '__unassigned__'). Built from the
    // resolved groups so it matches exactly how rows are bucketed in the UI.
    const choiceGroupKeyByName = useMemo(() => {
        const m = new Map<string, string>();
        for (const g of byoChoiceGroups) {
            const key = g.category?.id ?? BYO_UNASSIGNED_KEY;
            for (const c of g.choices) m.set(c.name, key);
        }
        return m;
    }, [byoChoiceGroups]);

    // All group keys present for this dish — used by the master weighted toggle.
    const allGroupKeys = useMemo(
        () => byoChoiceGroups.map(g => g.category?.id ?? BYO_UNASSIGNED_KEY),
        [byoChoiceGroups]
    );

    // --- Grouped ingredients ---
    const groupedIngredients = useMemo(() => groupIngredientsBySubrecipe(enrichedAnalysis), [enrichedAnalysis]);
    const nestedMenuItemKeys = Object.keys(groupedIngredients).filter(k => k.startsWith('nested-'));

    /**
     * Subrecipe reference rows live in enrichedAnalysis.ingredients as rows where
     * subrecipeId is set. Each maps 1:1 to one display entry. Display data (name,
     * yield, cost) is computed live by joining against the subrecipes store.
     */
    const subrecipeRefRows = useMemo(
        () => (enrichedAnalysis?.ingredients ?? []).filter((ing: any) => !!ing.subrecipeId && !ing.articleId),
        [enrichedAnalysis]
    );

    const subrecipesWithTotals = useMemo(() => {
        return subrecipeRefRows.map((ref: any) => {
            // A sibling-sourced subrecipe lives in that restaurant's data set, not
            // the current store — resolve its display info (name, yield) from there.
            const sub = ref.sourceRestaurantId && ref.sourceRestaurantId !== restaurantId
                ? sourceDataByRestaurant[ref.sourceRestaurantId]?.subrecipes.get(ref.subrecipeId)
                : subrecipesById.get(ref.subrecipeId);
            const unitCost = ref.currentPrice ?? 0;
            const portionsNeeded = ref.subrecipePortionsNeeded ?? 0;
            return {
                refRowId: ref.id,
                subrecipeId: ref.subrecipeId as string,
                name: sub?.name ?? t('costPrice.subrecipes.deletedSubrecipeLabel'),
                isOrphan: !sub,
                ingredients: [ref] as any[], // single ref row — display layer uses it for inline-edit state keying
                totalCost: unitCost * portionsNeeded,
                portionsNeeded,
                portionsUnit: sub?.portionsUnit ?? 'p',
                sourceRestaurantName: (ref as any).sourceRestaurantName as string | undefined,
            };
        });
    }, [subrecipeRefRows, subrecipesById, sourceDataByRestaurant, restaurantId, t]);

    const nestedMenuItemsWithTotals = useMemo(() => {
        return nestedMenuItemKeys.map(groupKey => {
            const ingredients = groupedIngredients[groupKey] || [];
            const info = getNestedCostPriceInfo(ingredients);
            return { name: info.name, quantityNeeded: info.quantityNeeded, totalCost: info.totalCost, nestedCostPriceId: groupKey.replace('nested-', '') };
        });
    }, [nestedMenuItemKeys, groupedIngredients]);

    // --- Derived values ---
    // Resolve the dish's type (FOOD/DRINKS/...) by mirroring dataAdapter:
    //   - linked dish (has menuItemName) → look up revenue source type from revenueHistory
    //   - unlinked custom dish → fall back to costPriceType
    const dishType = useMemo(() => {
        if (!enrichedAnalysis) return undefined;
        if (enrichedAnalysis.menuItemName) {
            for (const revenue of revenueHistoryStore.revenueHistoryList) {
                const categories = revenue.revenueData?.categories;
                if (!categories) continue;
                for (const category of categories) {
                    if (category.items.some(item => item.name === enrichedAnalysis.menuItemName)) {
                        return revenue.revenueSourceType || undefined;
                    }
                }
            }
        }
        return (enrichedAnalysis as any).costPriceType || undefined;
    }, [enrichedAnalysis, revenueHistoryStore.revenueHistoryList]);

    const dishTarget = getCostTargetForDishType(dishType, costTargets);
    const isCostBad = costPercentage ? isCostBadFor(costPercentage, dishTarget) : false;
    const profitMargin = effectiveMenuPrice ? menuPriceForCalculation - (enrichedAnalysis?.totalCost ?? 0) : 0;
    // Units sold:
    //   - Linked dish: trailing 2-month qty for its menuItemName
    //   - CHOICE row:  trailing 2-month qty of this choice across all BYO dishes
    //   - everything else: 0
    const unitsSold = useMemo(() => {
        if (!enrichedAnalysis) return 0;
        if (enrichedAnalysis.menuItemName) {
            return getQuantitySoldLast14Days(enrichedAnalysis.menuItemName, revenueHistoryStore.revenueHistoryList);
        }
        if (enrichedAnalysis.costPriceType === COST_PRICE_TYPE_CHOICE) {
            // Dish context: picks of this choice WITHIN the parent dish.
            if (dishScopedChoiceAgg) return dishScopedChoiceAgg.qtySold;
            const agg = getChoiceAvgUnitPrice(enrichedAnalysis.costPriceName, revenueHistoryStore.revenueHistoryList);
            return agg?.qtySold ?? 0;
        }
        return 0;
    }, [enrichedAnalysis, revenueHistoryStore.revenueHistoryList, dishScopedChoiceAgg]);

    // BYO composer derived values, computed PER add-on category:
    //   - weighted category   → folds in its own weighted-average contribution
    //                           (Σ perPickCost·qtySold / baseQty across its choices).
    //   - unweighted category → folds in only its picked choices (one pick each,
    //                           multi-select sums); €0 when nothing is picked.
    // When every category is weighted this exactly reconstructs the full weighted
    // average, so the headline is unchanged from the old default view.
    const composed = useMemo(() => {
        const byo = enrichedAnalysis?.byo;
        if (!byo || byo.baseQty <= 0) return null;
        const basePrice = showExVatOrders ? byo.baseItemPriceExVat : byo.baseItemPrice;
        let cost = byo.baseCost;
        let price = basePrice;
        for (const group of byoChoiceGroups) {
            const key = group.category?.id ?? BYO_UNASSIGNED_KEY;
            const isWeighted = !unweightedCategoryKeys.has(key);
            for (const c of group.choices) {
                const pickPrice = showExVatOrders ? c.unitPriceExVat : c.unitPrice;
                if (isWeighted) {
                    // Average contribution of this choice per dish sold.
                    cost += (c.perPickCost ?? 0) * c.qtySold / byo.baseQty;
                    price += pickPrice * c.qtySold / byo.baseQty;
                } else if (composerSelection.has(c.name)) {
                    cost += c.perPickCost ?? 0;
                    price += pickPrice;
                }
            }
        }
        const costPct = price > 0 ? (cost / price) * 100 : 0;
        return { cost, price, costPct };
    }, [enrichedAnalysis, byoChoiceGroups, unweightedCategoryKeys, composerSelection, showExVatOrders]);

    // Master "Weighted options" toggle. ON → every category weighted (clears all
    // picks). OFF → every category unweighted with no picks (base portion only).
    const setWeighted = useCallback((on: boolean) => {
        if (on) {
            setUnweightedCategoryKeys(new Set());
            setComposerSelection(new Set());
        } else {
            setUnweightedCategoryKeys(new Set(allGroupKeys));
            setComposerSelection(new Set());
        }
    }, [allGroupKeys]);

    // Per-category weighted toggle. ON → re-weight the category and drop its
    // picks. OFF → unweight it (its picks, if any, drive the contribution; €0
    // until the user picks something).
    const setCategoryWeighted = useCallback((groupKey: string, on: boolean) => {
        setUnweightedCategoryKeys(prev => {
            const next = new Set(prev);
            if (on) next.delete(groupKey); else next.add(groupKey);
            return next;
        });
        if (on) {
            setComposerSelection(prev => {
                const next = new Set(prev);
                choiceGroupKeyByName.forEach((key, name) => {
                    if (key === groupKey) next.delete(name);
                });
                return next;
            });
        }
    }, [choiceGroupKeyByName]);

    // Picking a choice unweights ONLY its category (the rest stay weighted) and
    // toggles the pick. Multi-select: multiple picks in a category sum together.
    const toggleComposerChoice = useCallback((name: string) => {
        const groupKey = choiceGroupKeyByName.get(name) ?? BYO_UNASSIGNED_KEY;
        setUnweightedCategoryKeys(prev => {
            if (prev.has(groupKey)) return prev;
            const next = new Set(prev);
            next.add(groupKey);
            return next;
        });
        setComposerSelection(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    }, [choiceGroupKeyByName]);

    // =========== HANDLERS ===========

    const reloadStore = useCallback(async () => {
        if (restaurantId) {
            await costPriceStore.load(restaurantId);
            await costPriceStore.reloadCustomIngredients();
        }
    }, [restaurantId, costPriceStore]);

    const handleIngredientAdded = useCallback(async () => { await reloadStore(); }, [reloadStore]);

    // --- BYO per-dish overrides (dish context only) ---
    // Merge-and-persist the parent dish's override map for THIS choice.
    // Empty maps are pruned; an empty top-level map clears the column (null).
    const persistChoiceOverrides = useCallback(async (mutate: (forChoice: Record<string, number>) => void) => {
        if (!parentAnalysis || !sharedAnalysis) return;
        const choiceName = sharedAnalysis.costPriceName;
        const all = { ...(parentAnalysis.byoChoiceOverrides ?? {}) };
        const forChoice = { ...(all[choiceName] ?? {}) };
        mutate(forChoice);
        if (Object.keys(forChoice).length === 0) delete all[choiceName];
        else all[choiceName] = forChoice;
        const updated = await updateCostPriceAnalysis({
            id: parentAnalysis.id,
            expectedUpdatedAt: recipeWriteVersion(parentAnalysis),
            byoChoiceOverrides: Object.keys(all).length > 0 ? all : null,
        });
        costPriceStore.upsertOne(updated);
    }, [parentAnalysis, sharedAnalysis, costPriceStore]);

    // Quantity edit in dish context → per-dish override. Entering the shared
    // quantity again removes the override (the row follows shared once more).
    const saveChoiceOverride = useCallback(async (row: CostPriceIngredient, quantity: number) => {
        const contentKey = choiceIngredientKey(row);
        const sharedRow = sharedAnalysis?.ingredients.find(r => choiceIngredientKey(r) === contentKey);
        const sharedQty = sharedRow
            ? ((sharedRow.subrecipeId && !sharedRow.articleId) ? sharedRow.subrecipePortionsNeeded
                : sharedRow.nestedCostPriceId ? sharedRow.nestedQuantityNeeded
                    : sharedRow.quantityNeeded)
            : undefined;
        await persistChoiceOverrides(forChoice => {
            if (sharedQty !== undefined && quantity === sharedQty) delete forChoice[contentKey];
            else forChoice[contentKey] = quantity;
        });
    }, [sharedAnalysis, persistChoiceOverrides]);

    // Reset one row back to the shared quantity. `sectionKey` is the key each
    // section component uses for its rows (ingredient.id / subrecipeId /
    // nestedCostPriceId).
    const handleResetChoiceOverride = useCallback(async (sectionKey: string) => {
        const row = sharedAnalysis?.ingredients.find(r =>
            r.id === sectionKey
            || (!!r.subrecipeId && !r.articleId && r.subrecipeId === sectionKey)
            || r.nestedCostPriceId === sectionKey
        );
        if (!row) return;
        try {
            await persistChoiceOverrides(forChoice => { delete forChoice[choiceIngredientKey(row)]; });
            // Drop any inline edit for this row so the restored shared quantity
            // shows instead of the stale typed value.
            const dropKey = (prev: Record<string, string>) => {
                const next = { ...prev };
                delete next[sectionKey];
                return next;
            };
            setInlineIngredientQuantities(dropKey);
            setInlineSubrecipeQuantities(dropKey);
            setInlineNestedMenuItemQuantities(dropKey);
        } catch (error) {
            if (error instanceof StaleRecipeWriteError) { alert(error.message); return; }
            console.error('Failed to reset choice override:', error);
        }
    }, [sharedAnalysis, persistChoiceOverrides]);

    const handleClose = useCallback(() => {
        setIsPanelVisible(false);
        setTimeout(onClose, 200);
    }, [onClose]);

    // --- Category ---
    const handleCategoryClick = useCallback(() => {
        if (!enrichedAnalysis || isPracticeId(enrichedAnalysis.id)) return;
        setEditingItemCategory({
            itemName: enrichedAnalysis.costPriceName,
            currentCategoryId: enrichedAnalysis.categoryId || categoriesStore.getDefaultCategory()?.id || ''
        });
    }, [enrichedAnalysis, categoriesStore]);

    const handleChangeItemCategory = useCallback(async (_itemName: string, newCategoryId: string) => {
        if (!restaurantId || !enrichedAnalysis) return;
        try {
            const updated = await updateCostPriceAnalysis({ id: enrichedAnalysis.id, categoryId: newCategoryId });
            costPriceStore.upsertOne(updated);
            setEditingItemCategory(null);
        } catch (error) {
            console.error('Failed to change item category:', error);
            alert('Failed to change item category');
        }
    }, [restaurantId, enrichedAnalysis, costPriceStore]);

    const handleCreateAndAssignCategory = useCallback(async (categoryName: string) => {
        if (isPracticeId(costPriceId) || !restaurantId || !categoryName.trim() || !editingItemCategory) return;
        try {
            // A CHOICE analysis creates add-on-scoped categories; everything else revenue.
            const scope = enrichedAnalysis?.costPriceType === COST_PRICE_TYPE_CHOICE ? 'addon' : 'revenue';
            const newCategory = await createCostPriceCategory({ restaurantId, name: categoryName.trim(), scope });
            await categoriesStore.reload();
            await handleChangeItemCategory(editingItemCategory.itemName, newCategory.id);
        } catch (error) {
            console.error('Failed to create and assign category:', error);
            alert('Failed to create and assign category');
        }
    }, [costPriceId, restaurantId, editingItemCategory, categoriesStore, handleChangeItemCategory, enrichedAnalysis]);

    const handleReorderCategories = useCallback(async (reorderedCategories: any[]) => {
        if (isPracticeId(costPriceId) || !restaurantId) return;
        try {
            const updates = reorderedCategories.filter(cat => !cat.isDefault).map(cat => ({ id: cat.id, position: cat.position }));
            await updateCategoryPositions(updates);
            await categoriesStore.reload();
        } catch (error) {
            console.error('Failed to reorder categories:', error);
            alert('Failed to reorder categories');
        }
    }, [costPriceId, restaurantId, categoriesStore]);

    // --- BYO add-on category assignment (operates on a choice's CHOICE analysis) ---
    const handleChoiceCategoryClick = useCallback((choiceName: string) => {
        setEditingChoiceCategory({
            choiceName,
            currentCategoryId: choiceCategoryIdByName.get(choiceName) || '',
        });
    }, [choiceCategoryIdByName]);

    const handleChangeChoiceCategory = useCallback(async (choiceName: string, newCategoryId: string) => {
        if (!restaurantId) return;
        try {
            // Lazily materialise the CHOICE row, then set its add-on category.
            const choice = await findOrCreateChoiceCostPrice(restaurantId, choiceName, undefined, costPriceStore.costPriceList);
            const updated = await updateCostPriceAnalysis({ id: choice.id, categoryId: newCategoryId });
            costPriceStore.upsertOne(updated);
            setEditingChoiceCategory(null);
        } catch (error) {
            console.error('Failed to change choice category:', error);
            alert('Failed to change choice category');
        }
    }, [restaurantId, costPriceStore]);

    const handleCreateAndAssignChoiceCategory = useCallback(async (categoryName: string) => {
        if (!restaurantId || !categoryName.trim() || !editingChoiceCategory) return;
        try {
            const newCategory = await createCostPriceCategory({ restaurantId, name: categoryName.trim(), scope: 'addon' });
            await categoriesStore.reload();
            await handleChangeChoiceCategory(editingChoiceCategory.choiceName, newCategory.id);
        } catch (error) {
            console.error('Failed to create and assign choice category:', error);
            alert('Failed to create and assign choice category');
        }
    }, [restaurantId, editingChoiceCategory, categoriesStore, handleChangeChoiceCategory]);

    // --- Revenue link ---
    const handleSaveRevenueLink = useCallback(async (
        newMenuItemName: string | null,
        opts?: { replaceExistingLink: boolean },
    ) => {
        if (!enrichedAnalysis || !restaurantId) return;
        setRevenueLinkRefreshFailed(false);
        try {
            const updated = await updateCostPriceAnalysis({
                id: enrichedAnalysis.id,
                menuItemName: newMenuItemName,
                replaceExistingLink: opts?.replaceExistingLink,
            });
            costPriceStore.upsertOne(updated);
            if (opts?.replaceExistingLink && !isPracticeId(enrichedAnalysis.id)) {
                // The move has committed. A failed refresh must not report a failed save.
                try {
                    await costPriceStore.reload();
                    setRevenueLinkRefreshFailed(!!useCostPriceStore.getState().error);
                } catch (error) {
                    console.error('Revenue link saved, but refreshing cost prices failed:', error);
                    setRevenueLinkRefreshFailed(true);
                }
            }
            setShowEditRevenueLinkModal(false);
        } catch (error) {
            console.error('Failed to update revenue link:', error);
            throw error;
        }
    }, [enrichedAnalysis, restaurantId, costPriceStore]);

    // --- Rename (costPriceName) ---
    // Mirrors handleSaveRevenueLink: persist + upsert, and RE-THROW on failure so
    // the inline editor can surface a duplicate-name error and stay in edit mode.
    // Empty or unchanged names are a no-op (returns without a request).
    const handleSaveCostPriceName = useCallback(async (newName: string) => {
        if (!enrichedAnalysis || !restaurantId) return;
        const trimmed = newName.trim();
        if (!trimmed || trimmed === enrichedAnalysis.costPriceName) return;
        try {
            const updated = await updateCostPriceAnalysis({ id: enrichedAnalysis.id, costPriceName: trimmed });
            costPriceStore.upsertOne(updated);
        } catch (error) {
            console.error('Failed to rename cost price:', error);
            throw error;
        }
    }, [enrichedAnalysis, restaurantId, costPriceStore]);

    // --- Ingredient CRUD ---
    const handleSaveEditIngredient = useCallback(async (ingredientId: string, newQuantityStr: string, originalQuantity: number) => {
        if (!enrichedAnalysis || !restaurantId) return;
        const quantityNeeded = parseFloat(newQuantityStr);
        if (isNaN(quantityNeeded) || quantityNeeded <= 0 || quantityNeeded === originalQuantity) return;
        try {
            // Dish context: the quantity becomes a per-dish override on the
            // parent BYO dish — the shared CHOICE stays untouched.
            if (isChoiceWithDishContext) {
                const row = sharedAnalysis?.ingredients.find(r => r.id === ingredientId);
                if (row) await saveChoiceOverride(row, quantityNeeded);
                return;
            }
            const current = await fetchCostPriceAnalysisById(enrichedAnalysis.id);
            const updatedIngredients = current.ingredients.map(ing =>
                ing.id === ingredientId
                    ? { ...mapIngredientForUpdate(ing), quantityNeeded }
                    : mapIngredientForUpdate(ing)
            );
            const updated = await updateCostPriceAnalysis({ id: enrichedAnalysis.id, expectedUpdatedAt: recipeWriteVersion(current), ingredients: updatedIngredients });
            costPriceStore.upsertOne(updated);
        } catch (error) {
            if (error instanceof StaleRecipeWriteError) { alert(error.message); return; }
            console.error('Failed to update ingredient:', error);
        }
    }, [enrichedAnalysis, restaurantId, costPriceStore, isChoiceWithDishContext, sharedAnalysis, saveChoiceOverride]);

    // Save a per-ingredient waste % edit. An empty string clears the override
    // (back to inherit); an explicit number (incl. 0) is stored as wastePercentage.
    const handleSaveEditIngredientWaste = useCallback(async (ingredientId: string, newWasteStr: string) => {
        if (!enrichedAnalysis || !restaurantId) return;
        const trimmed = newWasteStr.trim();
        let wastePercentage: number | undefined;
        if (trimmed === '') {
            wastePercentage = undefined; // inherit restaurant default
        } else {
            const parsed = parseFloat(trimmed);
            if (isNaN(parsed) || parsed < 0 || parsed > 100) return;
            wastePercentage = parsed;
        }
        try {
            const current = await fetchCostPriceAnalysisById(enrichedAnalysis.id);
            const updatedIngredients = current.ingredients.map(ing =>
                ing.id === ingredientId
                    ? { ...mapIngredientForUpdate(ing), wastePercentage }
                    : mapIngredientForUpdate(ing)
            );
            const updated = await updateCostPriceAnalysis({ id: enrichedAnalysis.id, expectedUpdatedAt: recipeWriteVersion(current), ingredients: updatedIngredients });
            costPriceStore.upsertOne(updated);
        } catch (error) {
            if (error instanceof StaleRecipeWriteError) { alert(error.message); return; }
            console.error('Failed to update ingredient waste:', error);
        }
    }, [enrichedAnalysis, restaurantId, costPriceStore]);

    // --- Alternatives (order-history ingredient rows only) ---
    // Structural, like add/remove: always edits the SHARED analysis (no per-dish
    // override), same as the waste handler. Cost math ignores alternatives.
    const openAlternatives = useCallback((ingredient: CostPriceIngredient | SubrecipeIngredient, snapshot?: CostPriceAnalysis) => {
        // Shared IngredientsSection is generic over both domains, but the cost-price modal
        // only opens alternatives on order-history CostPriceIngredient rows (the subrecipe
        // modal uses its own id-based handler), so this narrows safely.
        alternativesSnapshot.current = snapshot ?? sharedAnalysis ?? null;
        setAlternativesIngredient(ingredient as CostPriceIngredient);
    }, [sharedAnalysis]);

    const handleSaveAlternatives = useCallback(async (alternatives: CostPriceIngredient['alternatives']) => {
        if (!enrichedAnalysis || !restaurantId || !alternativesIngredient) return;
        // Optimistically update the open snapshot so the view reflects the edit
        // immediately (row ids regenerate server-side, so we can't re-derive it).
        setAlternativesIngredient((prev) => (prev ? { ...prev, alternatives } : prev));
        // Match the primary row by product identity (row ids are unstable across saves).
        // CONSTRAINT: matching is by productKey, not row id — so if a dish lists the SAME
        // product on two separate rows, both receive these alternatives. Accepted edge
        // (duplicate identical rows are already ambiguous for waste attribution).
        const primaryKey = productKey(
            alternativesIngredient.platform ?? '', alternativesIngredient.articleId, alternativesIngredient.productName
        );
        try {
            const current = alternativesSnapshot.current;
            if (!current) return;
            const updatedIngredients = current.ingredients.map(ing => {
                const isNormalRow = !ing.custom && !(ing.subrecipeId && !ing.articleId) && !ing.nestedCostPriceId;
                if (isNormalRow && productKey(ing.platform ?? '', ing.articleId, ing.productName) === primaryKey) {
                    return { ...mapIngredientForUpdate(ing), alternatives };
                }
                return mapIngredientForUpdate(ing);
            });
            const updated = await updateCostPriceAnalysis({ id: enrichedAnalysis.id, expectedUpdatedAt: recipeWriteVersion(current), ingredients: updatedIngredients });
            alternativesSnapshot.current = updated;
            costPriceStore.upsertOne(updated);
        } catch (error) {
            if (error instanceof StaleRecipeWriteError) {
                alternativesSnapshot.current = null;
                setAlternativesIngredient(null);
                alert(error.message);
                return;
            }
            console.error('Failed to save alternatives:', error);
        }
    }, [enrichedAnalysis, restaurantId, alternativesIngredient, costPriceStore]);

    // Swap an alternative with the primary: the alternative's product (with its own
    // quantity/unit/waste) becomes the ingredient row, and the old primary drops into
    // the alternatives list in the swapped-in product's place. Structural like the
    // handlers above: always edits the SHARED analysis; closes the Alternatives view
    // so the main modal immediately shows the new cost.
    const handleSwapAlternative = useCallback(async (alt: IngredientAlternative) => {
        if (!enrichedAnalysis || !restaurantId || !alternativesIngredient) return;
        const primaryKey = productKey(
            alternativesIngredient.platform ?? '', alternativesIngredient.articleId, alternativesIngredient.productName
        );
        const altKey = productKey(alt.platform, alt.articleId, alt.productName);
        try {
            const current = alternativesSnapshot.current;
            if (!current) return;
            const updatedIngredients = current.ingredients.map(ing => {
                const isNormalRow = !ing.custom && !(ing.subrecipeId && !ing.articleId) && !ing.nestedCostPriceId;
                if (!isNormalRow || productKey(ing.platform ?? '', ing.articleId, ing.productName) !== primaryKey) {
                    return mapIngredientForUpdate(ing);
                }
                // Demoted primary as an alternative (id regenerated server-side, like all
                // alternative ids). Fresh-fetched `ing` is the source so no live price fields leak.
                const demoted: IngredientAlternative = {
                    id: `temp-alt-${Date.now()}-${Math.random()}`,
                    articleId: ing.articleId ?? '',
                    productName: ing.productName ?? '',
                    platform: ing.platform ?? '',
                    customPlatformName: ing.customPlatformName,
                    quantityNeeded: ing.quantityNeeded ?? 0,
                    quantityInfo: ing.quantityInfo ?? { stdAmount: 1, stdMultiplier: 1, stdUnit: 'piece' },
                    wastePercentage: ing.wastePercentage,
                };
                return {
                    ...mapIngredientForUpdate(ing),
                    articleId: alt.articleId,
                    productName: alt.productName,
                    platform: alt.platform,
                    customPlatformName: alt.customPlatformName,
                    quantityNeeded: alt.quantityNeeded,
                    quantityInfo: alt.quantityInfo,
                    wastePercentage: alt.wastePercentage,
                    // Alternatives are always current-restaurant products, so the promoted
                    // row never keeps a sibling sourcing link.
                    sourceRestaurantId: undefined,
                    sourceRestaurantName: undefined,
                    // Match by productKey (alternative ids regenerate on every save).
                    alternatives: (ing.alternatives ?? []).map(a =>
                        productKey(a.platform, a.articleId, a.productName) === altKey ? demoted : a
                    ),
                };
            });
            const updated = await updateCostPriceAnalysis({ id: enrichedAnalysis.id, expectedUpdatedAt: recipeWriteVersion(current), ingredients: updatedIngredients });
            alternativesSnapshot.current = updated;
            costPriceStore.upsertOne(updated);
            setAlternativesIngredient(null);
        } catch (error) {
            if (error instanceof StaleRecipeWriteError) {
                alternativesSnapshot.current = null;
                setAlternativesIngredient(null);
                alert(error.message);
                return;
            }
            console.error('Failed to swap alternative:', error);
        }
    }, [enrichedAnalysis, restaurantId, alternativesIngredient, costPriceStore]);

    // --- Link a custom ingredient row to real purchase data ---
    // Structural, like alternatives and waste: always edits the SHARED analysis, never
    // a per-dish BYO override — a link changes what the row IS, not how much of it this
    // dish uses. The `custom_ingredients` row itself is deliberately left alone
    // (deleting it would cascade-delete its inventory assignments).
    const openCustomLink = useCallback((ingredient: CostPriceIngredient | SubrecipeIngredient) => {
        // Shared IngredientsSection is generic over both domains; the cost-price editor
        // only ever hands it CostPriceIngredient rows, so this narrows safely.
        setCustomLinkIngredient(ingredient as CostPriceIngredient);
    }, []);

    const handleApplyCustomLink = useCallback(async (candidate: SwapCandidate, quantity: number) => {
        if (!enrichedAnalysis || !customLinkIngredient) return;
        const current = await fetchCostPriceAnalysisById(enrichedAnalysis.id);
        // Row ids are stable between the store snapshot and this fetch (nothing wrote in
        // between); the identity fallback covers a concurrent edit that regenerated them.
        const oldKey = choiceIngredientKey(customLinkIngredient);
        const target = current.ingredients.find(row => row.id === customLinkIngredient.id)
            ?? current.ingredients.find(row => row.custom && !row.subrecipeId && !row.nestedCostPriceId
                && choiceIngredientKey(row) === oldKey);
        if (!target) throw new Error('Custom ingredient row not found in analysis');

        // README trap 6: `byo_choice_overrides` keys a non-subrecipe row as
        // `${platform}:${articleId}`, so re-pointing this row orphans any per-dish
        // quantity override on the old identity. Decided 2026-09-03 (folder README):
        // accept it — the dish falls back to the CHOICE's own quantity, so no wrong
        // number is produced and the stale key is inert JSONB. Logged, not silent.
        const orphanedIn = costPriceStore.costPriceList.filter(a =>
            a.byoChoiceOverrides?.[enrichedAnalysis.costPriceName]?.[oldKey] !== undefined);
        if (orphanedIn.length > 0) {
            console.warn(
                `[customLink] Linking "${target.productName}" orphans BYO quantity override "${oldKey}" on dish(es): `
                + orphanedIn.map(a => a.costPriceName).join(', ')
                + ' — accepted by design (folder README trap 6); those dishes fall back to the choice quantity.'
            );
        }

        const ingredients = current.ingredients
            .map(row => (row.id === target.id ? linkedCostPriceRow(row, candidate, quantity) : row))
            .map(mapIngredientForUpdate);
        const updated = await updateCostPriceAnalysis({ id: enrichedAnalysis.id, expectedUpdatedAt: recipeWriteVersion(current), ingredients });
        costPriceStore.upsertOne(updated);
    }, [enrichedAnalysis, customLinkIngredient, costPriceStore]);

    const handleConfirmDeleteIngredient = useCallback(async (ingredient: any) => {
        if (!enrichedAnalysis || !restaurantId) return;
        try {
            const current = await fetchCostPriceAnalysisById(enrichedAnalysis.id);
            const updatedIngredients = current.ingredients.filter(ing => ing.id !== ingredient.id).map(mapIngredientForUpdate);
            const updated = await updateCostPriceAnalysis({ id: enrichedAnalysis.id, expectedUpdatedAt: recipeWriteVersion(current), ingredients: updatedIngredients });
            costPriceStore.upsertOne(updated);
            setConfirmingDeleteIngredientId(null);
        } catch (error) {
            if (error instanceof StaleRecipeWriteError) { alert(error.message); return; }
            console.error('Failed to remove ingredient:', error);
            alert('Failed to remove ingredient');
            setConfirmingDeleteIngredientId(null);
        }
    }, [enrichedAnalysis, restaurantId, costPriceStore]);

    // Trigger handlers: automation users remove immediately (no confirm step),
    // everyone else gets the two-step "click icon → click Remove" confirm.
    const handleDeleteIngredient = useCallback((ingredient: any) => {
        if (ingredient.subrecipeId && !ingredient.articleId) {
            // Subrecipe reference row — caller should use handleDeleteSubrecipe instead.
            return;
        }
        if (canAccessAutomation) {
            handleConfirmDeleteIngredient(ingredient);
            return;
        }
        setConfirmingDeleteIngredientId(ingredient.id);
    }, [canAccessAutomation, handleConfirmDeleteIngredient]);

    // --- Nested menu item CRUD ---
    const handleSaveEditNestedMenuItem = useCallback(async (nestedCostPriceId: string, newQuantityStr: string, originalQuantity: number) => {
        if (!enrichedAnalysis || !restaurantId) return;
        const quantityNeeded = parseFloat(newQuantityStr);
        if (isNaN(quantityNeeded) || quantityNeeded <= 0 || quantityNeeded === originalQuantity) return;
        try {
            // Dish context: per-dish override on the parent BYO dish.
            if (isChoiceWithDishContext) {
                const row = sharedAnalysis?.ingredients.find(r => r.nestedCostPriceId === nestedCostPriceId);
                if (row) await saveChoiceOverride(row, quantityNeeded);
                return;
            }
            const current = await fetchCostPriceAnalysisById(enrichedAnalysis.id);
            const updatedIngredients = current.ingredients.map(ing =>
                (ing as any).nestedCostPriceId === nestedCostPriceId
                    ? { ...mapIngredientForUpdate(ing), quantityNeeded: 0, nestedQuantityNeeded: quantityNeeded }
                    : mapIngredientForUpdate(ing)
            );
            const updated = await updateCostPriceAnalysis({ id: enrichedAnalysis.id, expectedUpdatedAt: recipeWriteVersion(current), ingredients: updatedIngredients });
            costPriceStore.upsertOne(updated);
        } catch (error) {
            if (error instanceof StaleRecipeWriteError) { alert(error.message); return; }
            console.error('Failed to update nested menu item:', error);
        }
    }, [enrichedAnalysis, restaurantId, costPriceStore, isChoiceWithDishContext, sharedAnalysis, saveChoiceOverride]);

    const handleConfirmDeleteNestedMenuItem = useCallback(async (nestedCostPriceId: string, _menuItemName: string) => {
        if (!enrichedAnalysis || !restaurantId) return;
        try {
            const current = await fetchCostPriceAnalysisById(enrichedAnalysis.id);
            const updatedIngredients = current.ingredients
                .filter(ing => (ing as any).nestedCostPriceId !== nestedCostPriceId)
                .map(mapIngredientForUpdate);
            const updated = await updateCostPriceAnalysis({ id: enrichedAnalysis.id, expectedUpdatedAt: recipeWriteVersion(current), ingredients: updatedIngredients });
            costPriceStore.upsertOne(updated);
            setConfirmingDeleteNestedMenuItemId(null);
        } catch (error) {
            if (error instanceof StaleRecipeWriteError) { alert(error.message); return; }
            console.error('Failed to remove nested menu item:', error);
            alert('Failed to remove nested menu item');
            setConfirmingDeleteNestedMenuItemId(null);
        }
    }, [enrichedAnalysis, restaurantId, costPriceStore]);

    const handleDeleteNestedMenuItem = useCallback((nestedCostPriceId: string) => {
        if (canAccessAutomation) {
            handleConfirmDeleteNestedMenuItem(nestedCostPriceId, '');
            return;
        }
        setConfirmingDeleteNestedMenuItemId(nestedCostPriceId);
    }, [canAccessAutomation, handleConfirmDeleteNestedMenuItem]);

    // --- Subrecipe CRUD ---
    // Reference rows: edits change a single field (subrecipePortionsNeeded); deletes drop the one row.
    // The `subrecipeKey` argument is the subrecipeId (reference rows are keyed by it in the UI).
    const handleSaveEditSubrecipe = useCallback(async (subrecipeKey: string, newQuantityStr: string, originalPortionsNeeded: number) => {
        if (!enrichedAnalysis || !restaurantId) return;
        const quantityNeeded = parseFloat(newQuantityStr);
        if (isNaN(quantityNeeded) || quantityNeeded <= 0 || quantityNeeded === originalPortionsNeeded) return;
        try {
            // Dish context: per-dish override on the parent BYO dish.
            if (isChoiceWithDishContext) {
                const row = sharedAnalysis?.ingredients.find(r => !!r.subrecipeId && !r.articleId && r.subrecipeId === subrecipeKey);
                if (row) await saveChoiceOverride(row, quantityNeeded);
                return;
            }
            const current = await fetchCostPriceAnalysisById(enrichedAnalysis.id);
            const updatedIngredients = current.ingredients.map(ing => {
                if ((ing as any).subrecipeId === subrecipeKey && !(ing as any).articleId) {
                    return { ...mapIngredientForUpdate(ing), subrecipePortionsNeeded: quantityNeeded };
                }
                return mapIngredientForUpdate(ing);
            });
            const updated = await updateCostPriceAnalysis({ id: enrichedAnalysis.id, expectedUpdatedAt: recipeWriteVersion(current), ingredients: updatedIngredients });
            costPriceStore.upsertOne(updated);
        } catch (error) {
            if (error instanceof StaleRecipeWriteError) { alert(error.message); return; }
            console.error('Failed to update subrecipe:', error);
        }
    }, [enrichedAnalysis, restaurantId, costPriceStore, isChoiceWithDishContext, sharedAnalysis, saveChoiceOverride]);

    const handleConfirmDeleteSubrecipe = useCallback(async (subrecipeKey: string) => {
        if (!enrichedAnalysis || !restaurantId) return;
        try {
            const current = await fetchCostPriceAnalysisById(enrichedAnalysis.id);
            const updatedIngredients = current.ingredients
                .filter(ing => !((ing as any).subrecipeId === subrecipeKey && !(ing as any).articleId))
                .map(mapIngredientForUpdate);
            const updated = await updateCostPriceAnalysis({ id: enrichedAnalysis.id, expectedUpdatedAt: recipeWriteVersion(current), ingredients: updatedIngredients });
            costPriceStore.upsertOne(updated);
            setConfirmingDeleteSubrecipeName(null);
        } catch (error) {
            if (error instanceof StaleRecipeWriteError) { alert(error.message); return; }
            console.error('Failed to remove subrecipe:', error);
            alert('Failed to remove subrecipe');
            setConfirmingDeleteSubrecipeName(null);
        }
    }, [enrichedAnalysis, restaurantId, costPriceStore]);

    const handleDeleteSubrecipe = useCallback((subrecipeKey: string) => {
        if (canAccessAutomation) {
            handleConfirmDeleteSubrecipe(subrecipeKey);
            return;
        }
        setConfirmingDeleteSubrecipeName(subrecipeKey);
    }, [canAccessAutomation, handleConfirmDeleteSubrecipe]);

    // --- Menu price ---
    const handleSaveCustomMenuPrice = useCallback(async (customPrice: number, customPriceExVat: number) => {
        if (!enrichedAnalysis || !restaurantId) return;
        try {
            const updated = await updateCostPriceAnalysis({ id: enrichedAnalysis.id, customMenuPrice: customPrice, customMenuPriceExVat: customPriceExVat });
            costPriceStore.upsertOne(updated);
            setShowEditMenuPriceModal(false);
        } catch (error) {
            console.error('Failed to save custom menu price:', error);
            throw error;
        }
    }, [enrichedAnalysis, restaurantId, costPriceStore]);

    const handleResetCustomMenuPrice = useCallback(async () => {
        if (!enrichedAnalysis || !restaurantId) return;
        try {
            const updated = await updateCostPriceAnalysis({ id: enrichedAnalysis.id, customMenuPrice: null, customMenuPriceExVat: null });
            costPriceStore.upsertOne(updated);
            setShowEditMenuPriceModal(false);
        } catch (error) {
            console.error('Failed to reset custom menu price:', error);
            throw error;
        }
    }, [enrichedAnalysis, restaurantId, costPriceStore]);

    // --- Mark irrelevant (dish drops out of the setup-score denominators) ---
    const handleToggleIrrelevant = useCallback(async () => {
        if (!enrichedAnalysis) return;
        try {
            const updated = await updateCostPriceAnalysis({ id: enrichedAnalysis.id, markedIrrelevant: !enrichedAnalysis.markedIrrelevant });
            costPriceStore.upsertOne(updated);
        } catch (error) {
            console.error('Failed to toggle marked irrelevant:', error);
        }
    }, [enrichedAnalysis, costPriceStore]);

    // --- Delete ---
    const handleDeleteCostPrice = useCallback(async () => {
        if (!enrichedAnalysis || !restaurantId) return;
        try {
            await deleteCostPriceAnalysis(enrichedAnalysis.id);
            costPriceStore.removeOne(enrichedAnalysis.id);
            setShowDeleteModal(false);
            handleClose();
        } catch (error) {
            console.error('Failed to delete cost price analysis:', error);
            alert('Failed to delete cost price analysis');
        }
    }, [enrichedAnalysis, restaurantId, costPriceStore, handleClose]);

    // --- Type change ---
    const handleTypeChange = useCallback(async (newType: string | null) => {
        if (!enrichedAnalysis) return;
        setShowTypeSelector(false);
        try {
            const updated = await updateCostPriceAnalysis({ id: enrichedAnalysis.id, costPriceType: newType });
            costPriceStore.upsertOne(updated);
        } catch (err) {
            console.error('Failed to update cost price type:', err);
        }
    }, [enrichedAnalysis, costPriceStore]);

    // --- Clear an entire section (automation users only) ---
    // The trash icon in a section header opens a confirmation modal; confirming
    // strips every row of that section from the analysis in one update.
    const handleClearSection = useCallback((section: 'ingredients' | 'nested' | 'subrecipes') => {
        setClearSectionTarget(section);
    }, []);

    const handleConfirmClearSection = useCallback(async () => {
        if (!enrichedAnalysis || !restaurantId || !clearSectionTarget) return;
        try {
            const current = await fetchCostPriceAnalysisById(enrichedAnalysis.id);
            const kept = current.ingredients.filter(ing => {
                const isSubrecipeRef = !!(ing as any).subrecipeId && !(ing as any).articleId;
                const isNested = !!(ing as any).nestedCostPriceId;
                if (clearSectionTarget === 'ingredients') return isSubrecipeRef || isNested;
                if (clearSectionTarget === 'nested') return !isNested;
                return !isSubrecipeRef; // 'subrecipes'
            });
            const updated = await updateCostPriceAnalysis({ id: enrichedAnalysis.id, expectedUpdatedAt: recipeWriteVersion(current), ingredients: kept.map(mapIngredientForUpdate) });
            costPriceStore.upsertOne(updated);
            setClearSectionTarget(null);
        } catch (error) {
            if (error instanceof StaleRecipeWriteError) { alert(error.message); return; }
            console.error('Failed to clear section:', error);
            alert('Failed to clear section');
            setClearSectionTarget(null);
        }
    }, [enrichedAnalysis, restaurantId, clearSectionTarget, costPriceStore]);

    const clipboard = costPriceStore.ingredientClipboard;
    const canPasteIngredients = !practiceData && !!rawAnalysis && clipboard?.restaurantId === rawAnalysis.restaurantId;
    const hasCopyIngredients = !!rawAnalysis?.ingredients.some(row => !row.subrecipeId && !row.nestedCostPriceId);
    const hasCopySubrecipes = !!rawAnalysis?.ingredients.some(row => row.subrecipeId && !row.nestedCostPriceId);

    const handleCopyIngredients = (selection: CostPriceCopySelection) => {
        if (!rawAnalysis || practiceData) return;
        // Include the quantity currently in the input, even if its blur-save is still in flight.
        const rows = rawAnalysis.ingredients.map(row => {
            const value = row.subrecipeId ? inlineSubrecipeQuantities[row.subrecipeId] : inlineIngredientQuantities[row.id];
            const quantity = value === undefined ? undefined : Number(value);
            const copy = { ...row };
            if (quantity !== undefined && Number.isFinite(quantity) && quantity > 0) {
                if (row.subrecipeId) copy.subrecipePortionsNeeded = quantity;
                else copy.quantityNeeded = quantity;
            }
            const waste = inlineIngredientWaste[row.id];
            if (waste !== undefined && (waste.trim() === '' || (Number.isFinite(Number(waste)) && Number(waste) >= 0 && Number(waste) <= 100))) {
                copy.wastePercentage = waste.trim() === '' ? undefined : Number(waste);
            }
            return copy;
        });
        const ingredients = copyCostPriceIngredients(rows, selection);
        if (!ingredients.length) return;
        costPriceStore.setIngredientClipboard({ restaurantId: rawAnalysis.restaurantId, sourceName: rawAnalysis.costPriceName, ingredients });
        setClipboardFeedback(t('costPrice.clipboard.copied', {
            ingredients: ingredients.filter(row => !row.subrecipeId).length,
            subrecipes: ingredients.filter(row => row.subrecipeId).length,
        }));
    };

    const handlePasteIngredients = async () => {
        if (!canPasteIngredients || !rawAnalysis || !clipboard || pasteInFlight.current) return;
        pasteInFlight.current = true;
        setIsPasting(true);
        setClipboardFeedback('');
        try {
            const current = await fetchCostPriceAnalysisById(rawAnalysis.id);
            if (current.restaurantId !== clipboard.restaurantId) return;
            const merged = mergeCostPriceClipboard(current.ingredients, clipboard, current.restaurantId);
            if (merged.added) {
                const updated = await updateCostPriceAnalysis({
                    id: current.id,
                    expectedUpdatedAt: recipeWriteVersion(current),
                    ingredients: merged.ingredients,
                });
                costPriceStore.upsertOne(updated);
            }
            setClipboardFeedback(t('costPrice.clipboard.pasted', { added: merged.added, skipped: merged.skipped }));
        } catch (error) {
            console.error('Failed to paste cost price ingredients:', error);
            setClipboardFeedback(error instanceof StaleRecipeWriteError ? error.message : t('costPrice.clipboard.failed'));
        } finally {
            pasteInFlight.current = false;
            setIsPasting(false);
        }
    };

    return {
        hasCopyIngredients, hasCopySubrecipes, handleCopyIngredients,
        canPasteIngredients, handlePasteIngredients, isPasting, clipboardFeedback,
        clipboardSourceName: clipboard?.sourceName,
        // Analysis data
        enrichedAnalysis,
        effectiveMenuPrice,
        menuPriceForCalculation,
        costPercentage,
        currentRevenuePrice,
        matrix,
        category,
        isCostBad,
        profitMargin,
        unitsSold,
        canAccessAutomation,

        // Grouped data
        groupedIngredients,
        subrecipesWithTotals,
        nestedMenuItemsWithTotals,
        addonCategories,
        byoChoiceGroups,

        // UI state
        isPanelVisible,
        isMaximized, setIsMaximized,
        showAddIngredients, setShowAddIngredients,
        selectedSubRecipeName, setSelectedSubRecipeName,
        alternativesIngredient, setAlternativesIngredient,
        openAlternatives, handleSaveAlternatives, handleSwapAlternative,
        customLinkIngredient, setCustomLinkIngredient,
        openCustomLink, handleApplyCustomLink,
        editingItemCategory, setEditingItemCategory,
        editingChoiceCategory, setEditingChoiceCategory,
        showEditRevenueLinkModal, setShowEditRevenueLinkModal,
        showEditMenuPriceModal, setShowEditMenuPriceModal,
        showDeleteModal, setShowDeleteModal,
        showTypeSelector, setShowTypeSelector,
        clearSectionTarget, setClearSectionTarget,
        weightedMode, composerMode, composerSelection, composed,
        unweightedCategoryKeys,
        setWeighted, setCategoryWeighted, toggleComposerChoice,
        revenueItems,
        restaurantId,

        // BYO dish context (CHOICE opened from a BYO dish)
        isChoiceWithDishContext,
        byoDishContextName: isChoiceWithDishContext ? parentAnalysis?.costPriceName : undefined,
        overriddenSectionKeys,
        handleResetChoiceOverride,

        // Inline editing state
        inlineIngredientQuantities, setInlineIngredientQuantities,
        inlineIngredientWaste, setInlineIngredientWaste,
        inlineNestedMenuItemQuantities, setInlineNestedMenuItemQuantities,
        inlineSubrecipeQuantities, setInlineSubrecipeQuantities,

        // Delete confirmation state
        confirmingDeleteIngredientId,
        confirmingDeleteNestedMenuItemId,
        confirmingDeleteSubrecipeName,

        // Store refs
        categoriesStore,

        // Handlers
        handleClose,
        handleIngredientAdded,
        handleCategoryClick,
        handleChangeItemCategory,
        handleCreateAndAssignCategory,
        handleReorderCategories,
        handleChoiceCategoryClick,
        handleChangeChoiceCategory,
        handleCreateAndAssignChoiceCategory,
        handleSaveRevenueLink,
        revenueLinkRefreshFailed,
        handleSaveCostPriceName,
        handleSaveEditIngredient,
        handleSaveEditIngredientWaste,
        handleDeleteIngredient,
        handleConfirmDeleteIngredient,
        handleSaveEditNestedMenuItem,
        handleDeleteNestedMenuItem,
        handleConfirmDeleteNestedMenuItem,
        handleSaveEditSubrecipe,
        handleDeleteSubrecipe,
        handleConfirmDeleteSubrecipe,
        handleSaveCustomMenuPrice,
        handleResetCustomMenuPrice,
        handleToggleIrrelevant,
        handleDeleteCostPrice,
        handleTypeChange,
        handleClearSection,
        handleConfirmClearSection,
    };
}
